"use client";

import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { Field, FieldLabel, FieldGroup, FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { AlertDialogContent, AlertDialog, AlertDialogDescription, AlertDialogTitle, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { useMutation } from "@tanstack/react-query";
import { accountAxiosInstance } from "@/services/axios";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import ImageComponent from "@/components/ui/image";
import tooffiLogo from "@/assets/images/tooffi-logo.svg";
import { useUser } from "@/contexts/user-context";
import { X } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { pushIdentifyToDataLayer } from "@/utils/analytics";
const EmailPopup = () => {
  const {
    isAuthenticated,
    isAuthLoading
  } = useUser();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{
    email?: string;
    phone?: string;
  }>({});
  const {
    mutate: shareEmail
  } = useMutation({
    mutationFn: (body: {
      email: string;
      mobile_number?: string;
    }) => {
      return accountAxiosInstance.post("/free-signup/", body);
    },
    onSuccess: (_data, variables) => {
      setIsSuccess(true);
      localStorage.setItem("email-popup-user-filled", "true");
      pushIdentifyToDataLayer({
        email: variables?.email || email,
        phone: variables?.mobile_number || phone,
        source: "popup"
      });
    },
    onError: error => {
      console.error("Error submitting email:", error);
      toast.error((error as AxiosError<{
        email: string[];
      }>)?.response?.data?.email?.[0] || "Error submitting. Please try again.");
    }
  });
  useEffect(() => {
    if (isAuthLoading) return;
    if (isAuthenticated) {
      localStorage.setItem("email-popup-user-filled", "true");
      return;
    }
    if (pathname?.startsWith("/partner-with-us")) {
      return;
    }
    try {
      const isEmailPopupClosed = sessionStorage.getItem("email-popup-closed");
      const isFormFilled = localStorage.getItem("email-popup-user-filled");
      if (isEmailPopupClosed || isFormFilled) {
        return;
      }
    } catch (error_0) {
      console.error("Error getting email popup closed status:", error_0);
    }
    let checkTimerId: NodeJS.Timeout | null = null;

    // Changed to 20s: wait 20 seconds before showing the popup
    const delayBeforeOpen = 15000;
    const retryInterval = 1000;
    let isWaitingForModalToClose = false;
    const isAnyModalOpen = () => document.body.style.pointerEvents === "none" || document.body.hasAttribute("data-scroll-locked") || document.querySelectorAll('[role="dialog"]:not([data-state="closed"]), [role="alertdialog"]:not([data-state="closed"]), [aria-modal="true"]:not([data-state="closed"])').length > 0 ||
    // Catch custom Tailwind overlays that are visible
    Array.from(document.querySelectorAll(".fixed.inset-0, .fixed.w-full.h-full")).some(el => {
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0" && (!el.hasAttribute("data-state") || el.getAttribute("data-state") === "open");
    });
    const initialTimerId = setTimeout(() => {
      const checkTimer = () => {
        if (isAnyModalOpen()) {
          isWaitingForModalToClose = true;
          checkTimerId = setTimeout(checkTimer, retryInterval);
        } else {
          if (isWaitingForModalToClose) {
            isWaitingForModalToClose = false;
            checkTimerId = setTimeout(checkTimer, delayBeforeOpen);
          } else {
            // Final guard: re-check one more time right before opening,
            // in case a lazy-loaded modal mounted after the previous check.
            if (!isAnyModalOpen()) {
              setIsOpen(true);
            } else {
              isWaitingForModalToClose = true;
              checkTimerId = setTimeout(checkTimer, retryInterval);
            }
          }
        }
      };
      checkTimer();
    }, delayBeforeOpen);
    return () => {
      clearTimeout(initialTimerId);
      if (checkTimerId) clearTimeout(checkTimerId);
    };
  }, [isAuthenticated, isAuthLoading, pathname]);
  useEffect(() => {
    if (isOpen) {
      // Strong scroll lock for body
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [isOpen]);
  const handleSubmit = () => {
    let hasError = false;
    const newErrors: {
      email?: string;
      phone?: string;
    } = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
      hasError = true;
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
      hasError = true;
    } else if (phone.replace(/\D/g, "").length < 10) {
      newErrors.phone = "Please enter a valid phone number";
      hasError = true;
    }
    if (hasError) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    shareEmail({
      email,
      mobile_number: phone
    });
  };
  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => setIsSuccess(false), 300);
    setEmail("");
    setPhone("");
    setErrors({});
    sessionStorage.setItem("email-popup-closed", "true");
  };
  if (isAuthLoading || isAuthenticated || pathname?.startsWith("/partner-with-us")) return null;
  return <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <form>
        <AlertDialogContent className="bg-white p-0 border-none w-screen h-[100dvh] max-w-none rounded-none sm:rounded-none md:rounded-none lg:w-auto lg:h-auto lg:max-w-4xl lg:rounded-lg overflow-y-auto overflow-x-hidden">
          <button type="button" className="flex absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 items-center justify-center border-none bg-transparent outline-none cursor-pointer z-50" onClick={handleClose} aria-label="Close">
            <X size={20} />
          </button>
          <div className="flex flex-col lg:flex-row gap-4 h-full min-h-full">
            {isSuccess ? <div className="p-8 md:p-12 flex flex-col justify-center flex-1 text-center items-center max-w-md mx-auto w-full h-full min-h-[50vh] lg:min-h-[400px]">
                <AlertDialogTitle className="font-lora text-4xl lg:text-[2.5rem] text-[#333333] mb-6 font-normal">
                  Success!
                </AlertDialogTitle>
                <p className="text-[#666666] text-lg lg:text-xl mb-6">
                  Thanks for signing up to our newsletter.
                </p>
                <AlertDialogDescription className="text-[#888888] text-sm lg:text-base leading-relaxed">
                  We've just sent you an email with your first-order discount.
                  Please allow a few minutes for it to arrive in your inbox (and
                  don't forget to check your junk folder just in case)
                </AlertDialogDescription>
                <Button type="button" className="mt-8 bg-[#037a6a] text-white hover:bg-[#037a6a]/90 w-full" onClick={() => {
              handleClose();
              router.push("/");
            }}>
                  Explore Tooffi
                </Button>
              </div> : <div className="p-6 flex flex-col justify-center flex-1">
                <ImageComponent src={tooffiLogo} alt="tooffi" width={128} height={48} sizes="128px" style={{
              width: "auto",
              height: "auto",
              maxWidth: "128px"
            }} className="mx-auto" />
                <AlertDialogTitle className="uppercase text-center mt-6 text-xl sm:text-2xl lg:text-[1.625rem] lg:whitespace-nowrap leading-tight font-medium lg:font-bold text-gray-700 lg:text-inherit">
                  GET 15% off on your first order
                </AlertDialogTitle>
                <AlertDialogDescription className="leading-relaxed mt-3 text-center">
                  Thoughtfully curated gifts from India's best makers, delivered
                  without the overwhelm.
                </AlertDialogDescription>
                <FieldGroup className="mt-6">
                  <Field>
                    <FieldLabel htmlFor="marketing-email">
                      Email
                      <span className="text-red-500 text-lg leading-none ml-1">
                        *
                      </span>
                    </FieldLabel>
                    <Input id="marketing-email" autoComplete="off" placeholder="Enter your email" value={email} onChange={e => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({
                    ...errors,
                    email: undefined
                  });
                }} type="email" required />
                    {errors.email && <FieldError className="text-red-500">
                        {errors.email}
                      </FieldError>}
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="phone">
                      Phone
                      <span className="text-red-500 text-lg leading-none ml-1">
                        *
                      </span>
                    </FieldLabel>
                    <Input id="phone" autoComplete="off" placeholder="Enter your phone number" value={phone} onChange={e_0 => {
                  setPhone(e_0.target.value);
                  if (errors.phone) setErrors({
                    ...errors,
                    phone: undefined
                  });
                }} type="tel" required />
                    {errors.phone && <FieldError className="text-red-500">
                        {errors.phone}
                      </FieldError>}
                  </Field>
                </FieldGroup>
                <div className="mt-6 flex flex-col items-center w-full gap-2">
                  <Button type="button" className="bg-[#037a6a] text-white w-full hover:bg-[#037a6a]/90" onClick={handleSubmit}>
                    Send My Discount
                  </Button>
                  <AlertDialogCancel asChild>
                    <Button variant="ghost" type="button" className="mt-2 text-sm text-gray-500 w-full hover:text-gray-700 hover:bg-transparent">
                      No thanks
                    </Button>
                  </AlertDialogCancel>
                  <p className="text-xs text-center text-gray-500 mt-3">
                    One email. No spam. Unsubscribe anytime.
                  </p>
                </div>
              </div>}
          </div>
        </AlertDialogContent>
      </form>
    </AlertDialog>;
};
export default EmailPopup;